import { Suspense, useCallback, useEffect, useRef, useState } from 'react';

import { PhoneIcon } from 'lucide-react';

import { Button, ErrorBoundaryWithRetry } from '@/_components';
import type { PhoneNumberQuerySchema } from '@/_schemas/phone-numbers';

import type { PhoneNumberRef } from './types';

import { SectionHeading } from '../../../headers';
import { NoDataMessage } from '../../../no-data-message';
import { SortableList } from '../../../sortable-list';
import type { PhoneNumberProps } from '../types';

import { AddPhoneNumberSuspense, PreviewPhoneNumber, SingleLoader } from '.';

export function PhoneNumber(props: PhoneNumberProps) {
  const { value, isEditable, isSortable = false, onChange } = props;
  const [newPns, setNewPns] = useState([1]);
  const phoneNumberRefs = useRef<(PhoneNumberRef | null)[]>([]);

  const validatePhoneNumbers = useCallback(
    async (isNew = false) => {
      if (!phoneNumberRefs.current) {
        return true;
      }
      const validationResults = await Promise.all(
        phoneNumberRefs.current.map(async (ref) => {
          if (ref) {
            return await ref.validate(isNew);
          }
          return true;
        })
      );
      return validationResults.every((result) => result);
    },
    [phoneNumberRefs]
  );

  const getDuplicatePhoneNumbers = useCallback(() => {
    const duplicatePhoneNumbers: string[] = [];
    if (!phoneNumberRefs.current) {
      return duplicatePhoneNumbers;
    }
    for (let i = 0; i < phoneNumberRefs.current.length; i++) {
      const ref = phoneNumberRefs.current[i];
      if (ref && ref.number) {
        for (let j = i + 1; j < phoneNumberRefs.current.length; j++) {
          const _ref = phoneNumberRefs.current[j];
          if (_ref && _ref.number && _ref.number === ref.number) {
            duplicatePhoneNumbers.push(ref.number);
          }
        }
      }
    }
    return duplicatePhoneNumbers;
  }, [phoneNumberRefs]);

  const validateDuplicatePhoneNumber = useCallback(
    async (phoneNumber: string) => {
      await validatePhoneNumbers();
      const duplicates = await getDuplicatePhoneNumbers();
      const isDuplicate = duplicates.some((number) => number === phoneNumber);
      return isDuplicate;
    },
    [getDuplicatePhoneNumbers, validatePhoneNumbers]
  );

  const handleNew = async () => {
    const phonesValid = await validatePhoneNumbers(true);
    if (!phonesValid) {
      return;
    }

    setNewPns((s) => [...s, 1]);
  };

  const handleDelete = (id: number) => {
    setNewPns((s) => {
      s = [...s];
      s[id] = 0;
      return s;
    });
  };

  const renderPhoneNumbers = useCallback(
    (item: PhoneNumberQuerySchema, index: number) => {
      if (!item.country) {
        return null;
      }

      const PhoneNumberComponent = (
        <PreviewPhoneNumber
          ref={(el) => {
            if (!phoneNumberRefs.current) {
              return;
            }
            phoneNumberRefs.current[index] = el;
          }}
          key={item.id}
          index={index}
          {...props}
          value={value ?? []}
          validateDuplicatePhoneNumber={validateDuplicatePhoneNumber}
        />
      );

      return (
        <ErrorBoundaryWithRetry key={`${item.id}${item.number}${index}`}>
          <Suspense fallback={<SingleLoader />}>
            {isSortable ? (
              <SortableList.Item key={item.id} id={item.id} index={index}>
                {isEditable && <SortableList.DragHandle />}
                {PhoneNumberComponent}
              </SortableList.Item>
            ) : (
              PhoneNumberComponent
            )}
          </Suspense>
        </ErrorBoundaryWithRetry>
      );
    },
    [isEditable, isSortable, props, value, validateDuplicatePhoneNumber]
  );

  useEffect(() => {
    if (!isEditable) {
      setNewPns([1]);
    }
  }, [isEditable]);

  return (
    <>
      <SectionHeading
        icon={PhoneIcon}
        text="Phone Numbers"
        rightSlot={
          isEditable ? (
            <Button size="xs" onClick={handleNew}>
              Add
            </Button>
          ) : undefined
        }
      />
      <article className="mt-6">
        {!isEditable && (value?.length === 0 || !value) && <NoDataMessage message="No Phone Number" />}

        {!!value && value.length > 0 && (
          <section className={`${isEditable ? 'mb-6' : ''} flex flex-col gap-1`}>
            <SortableList items={value} onChange={onChange('SORT')} renderItem={renderPhoneNumbers} />
          </section>
        )}

        {isEditable && (
          <article className="flex flex-col gap-1">
            {newPns.map(
              (item, i) =>
                item === 1 && (
                  <div key={i} className="flex flex-row">
                    {isSortable && value && value.length > 0 && <div className="h-2 w-9" />}
                    <AddPhoneNumberSuspense
                      ref={(el: PhoneNumberRef | null) => {
                        if (!phoneNumberRefs.current) {
                          return;
                        }
                        phoneNumberRefs.current[(value || []).length + i] = el;
                      }}
                      isLast={i === newPns.length - 1}
                      id={i}
                      {...props}
                      onDelete={handleDelete}
                      validateDuplicatePhoneNumber={validateDuplicatePhoneNumber}
                    />
                  </div>
                )
            )}
          </article>
        )}
      </article>
    </>
  );
}
